import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Toolbar from '@mui/material/Toolbar';
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import HorizontalSelectableCards from "../../components/HorizontalSelectableCards";

import PageTemplate from '../../components/PageTemplate';

import Timeline from '../../components/Bookings/Timeline';
import TripCompanions from '../../components/TripCompanions';
import TripComments from '../../components/TripComments';
import TripPreview from '../../components/TripPreview';
import { filterByProperty, pickRandomStringWithoutRepeating } from "../../../utils";
import { rankByInterests } from "../../../utils/interestTags";
import {
  buildItinerarySnapshot,
  canInviteToItinerary,
  whyNotShareable,
} from '../../../utils/tripSharing';
import { publishSnapshotToInvites, sendFinalCopies, upsertShare } from '../../../utils/tripInvites';
import { SHARED_ITINERARY_SUBJECT, buildSharedItineraryEmailHtml } from '../../../utils/tripEmails';
import { useApp } from '../../../context';
import { scrollableTabsSx } from '../../../theme';
import { timelineArrivalData, timelineMorningData, timelineAfternoonData, timelineEveningData } from "./data";

import './styles.css';

// `div`, not `main` — PageTemplate already provides the page's single <main> landmark.
const Main = styled('div')(({ theme }) => ({
  flexGrow: 1,
  height: '100vh',
  overflowY: 'auto',
  padding: theme.spacing(0),
  paddingBottom: theme.spacing(1),
  // Tokenized surface so the page is correct in dark mode (was a hardcoded #fff).
  background: theme.palette.background.default,
}));

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body1,
  textAlign: 'left',
  color: theme.palette.text.secondary,
  padding: theme.spacing(2),
  margin: theme.spacing(2),
}));

/** Label for the finalise button. Extracted only to keep the JSX readable. */
function finalised_label(finalized, finalising) {
  if (finalising) return 'Working…';
  return finalized ? 'Finalised' : 'Finalise Itinerary';
}

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`itinerary-tabpanel-${index}`}
      aria-labelledby={`itinerary-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function ItineraryPage() {
  const { id } = useParams();
  const { itineraryList, serviceListing, user, getItineraryData, updateItineraryAccommodation, saveItineraryTravelers, saveItinerarySchedule, finalizeItinerary, deleteItinerary } = useApp();

  const [dates, setDates] = useState({ start: '', end: '' });
  const [itinerary_, setItinerary] = useState(null);
  const [numberOfDays, setNumberOfDays] = useState(1);
  const [value, setValue] = React.useState(0);
  const [accomodationList, setAccomodationList] = React.useState([]);
  const [schedule, setSchedule] = React.useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  const [adults, setAdults] = React.useState(1);
  const [children, setChildren] = React.useState(0);
  const [infants, setInfants] = React.useState(0);

  // Sharing state. `shareId` and `invites` are lifted here rather than left
  // inside TripCompanions because finalising needs both: the snapshot has to be
  // republished so guests stop seeing a draft, and the final copy has to reach
  // everyone still on the list.
  const [share, setShare] = useState(null);
  const [invites, setInvites] = useState([]);
  const [finalising, setFinalising] = useState(false);
  const [finaliseError, setFinaliseError] = useState('');
  const [sentNotice, setSentNotice] = useState('');

  const handleShareReady = React.useCallback((nextShare, list) => {
    setShare(nextShare || null);
    setInvites(list || []);
  }, []);

  /**
   * Whether the signed-in traveller created this itinerary.
   *
   * Everything that CHANGES the trip hangs off this — finalising above all,
   * which the brief reserves for the creator. Today this is belt and braces:
   * the rules only let an owner read their own itinerary, so nobody else
   * reaches this page. It stops being belt and braces the moment a companion
   * is ever given a signed-in view, and the guard should already be here when
   * that happens rather than being remembered afterwards.
   *
   * Written so that an unloaded itinerary or a signed-out visitor is NOT the
   * owner, rather than defaulting to true and being narrowed later.
   */
  const isOwner = !!(user?.uid && itinerary_?.uid && user.uid === itinerary_.uid);


  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleAdults = (e) => {
    setAdults(e.target.value)
  };

  const handleChildren = (e) => {
    setChildren(e.target.value)
  };

  const handleInfants = (e) => {
    setInfants(e.target.value);
  };

  const saveTravelers = async () => {
    const travelers = { adults, children, infants };
    await saveItineraryTravelers(itinerary_.id, travelers);
    // Guests are looking at a party size too; republish so the shared copy and
    // the owner's screen do not disagree about how many people are going.
    publishShare({ ...itinerary_, travelers }, schedule);
  };

  const selectAccommodation = async (newValue) => {
    const accommodation = {
      contact: newValue?.contact,
      name: newValue?.name,
      type: newValue?.type,
      location: newValue?.location,
      id: newValue?.id,
      cost: newValue?.cost,
      status: "pending",//confirmed, pending, cancelled,
      dates: itinerary_.dates
    };

    await updateItineraryAccommodation(itinerary_.id, accommodation);
    // Where everyone is sleeping is the single change companions most want to
    // see, so it is republished immediately rather than at finalise.
    publishShare({ ...itinerary_, bookings: { accommodation } }, schedule);
  };

  /**
   * Finalises, republishes what guests see, and posts the final copy.
   *
   * Order matters. The itinerary is written first, because that is the thing
   * that must be true; the share and the emails follow. If either of those
   * fails the trip is still finalised, and the traveller is told what did not
   * happen rather than being shown a failure for a thing that worked.
   */
  const handleFinalizeItinerary = async (finalized) => {
    // Belt and braces. The button is not rendered for a non-owner, the rules
    // refuse the write, and this stops it before either is reached.
    if (!isOwner) {
      setFinaliseError('Only the person who created this trip can finalise it.');
      return;
    }

    setFinaliseError('');
    setFinalising(true);

    try {
      // Awaited and allowed to throw. finalizeItinerary used to swallow every
      // failure and resolve regardless, so everything below ran on a trip that
      // had not been finalised — republishing "confirmed" to the guests and
      // emailing them to say so.
      await finalizeItinerary(itinerary_.id, finalized);
    } catch (error) {
      console.error('handleFinalizeItinerary: could not finalise', error);
      // "Nothing was sent" is about the guests, and is true. It deliberately
      // does not claim nothing happened: finalising books the accommodation
      // before it writes the flag, so a failure between the two leaves a real
      // booking. Retrying is safe because createBooking is idempotent per
      // itinerary — the second attempt returns the booking the first one made
      // rather than a duplicate — and the traveller is told so, because a
      // person who suspects they have half-booked something will not press it
      // again on their own.
      setFinaliseError(
        'We could not finalise your trip just now, and nothing was sent to the people you invited. Please try again — you will not be booked twice.'
      );
      setFinalising(false);
      return;
    }

    try {
      if (share?.shareId) {
        // Guests must not keep seeing "being planned" against a settled trip.
        const snapshot = buildItinerarySnapshot({ ...itinerary_, finalized }, schedule);
        await upsertShare({
          ownerUid: user.uid,
          snapshot,
          existingShareId: share.shareId,
          threadId: share.threadId,
        });
        await publishSnapshotToInvites({ shareId: share.shareId, snapshot, invites });
      }

      // Only on the way in, and only to people still on the list. Un-finalising
      // is a correction, and mailing "your trip is confirmed" for a second time
      // because someone toggled a button twice is noise.
      if (finalized && !itinerary_.finalized && invites.length) {
        const snapshot = buildItinerarySnapshot({ ...itinerary_, finalized: true }, schedule);
        const sent = await sendFinalCopies({
          invites,
          subject: SHARED_ITINERARY_SUBJECT,
          buildHtml: (invite) =>
            buildSharedItineraryEmailHtml({
              snapshot,
              invite,
              organiser: 'Your trip organiser',
            }),
        });
        if (sent) {
          setSentNotice(
            sent === 1
              ? 'The final copy is on its way to 1 person.'
              : `The final copy is on its way to ${sent} people.`
          );
        }
      }
    } catch (error) {
      console.error('handleFinalizeItinerary: finalised, but sharing failed', error);
      setFinaliseError(
        'Your trip is finalised, but we could not update everyone you invited. Please try again.'
      );
    } finally {
      setFinalising(false);
    }
  };

  /**
   * Republishes the shared copy after a change the guests should see.
   *
   * Fire-and-forget: a companion looking at a slightly stale plan is a much
   * smaller problem than an owner blocked from picking a lodge because a
   * secondary write failed.
   */
  const publishShare = React.useCallback(
    (nextItinerary, nextSchedule) => {
      if (!share?.shareId || !user?.uid) return;
      const snapshot = buildItinerarySnapshot(nextItinerary, nextSchedule);
      // Both halves matter. The share is the owner's record to republish from;
      // the invites are what the guests actually read, so a publish that stops
      // at the share is a publish nobody sees.
      Promise.all([
        upsertShare({
          ownerUid: user.uid,
          snapshot,
          existingShareId: share.shareId,
          threadId: share.threadId,
        }),
        publishSnapshotToInvites({ shareId: share.shareId, snapshot, invites }),
      ]).catch((error) => console.warn('publishShare: could not refresh shared copy', error));
    },
    [share, user, invites]
  );

  useEffect(() => {
    if (!itineraryList.length) {
      getItineraryData()
    } else {
      const data = itineraryList.find(item => item.id === id);
      setItinerary(data)
    }
    // `id` comes from the route and cannot change without remounting this page;
    // `getItineraryData` is unmemoised on the context and sets state, so
    // listing it loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryList])

  useEffect(() => {
    if (itinerary_ && itinerary_.dates) {
      let dateB = '';
      let dateC = '';
      if (itinerary_.dates && itinerary_.dates.length > 1 && Array.isArray(itinerary_.dates)) {
        // dates is an array of start/end timestamps
        const formattedDates = itinerary_.dates.map(ts => moment(ts.seconds * 1000).format("DD MMM YYYY"));
        dateB = moment(formattedDates[0]);
        dateC = moment(formattedDates[formattedDates.length - 1]);
        setDates({
          start: formattedDates[0],
          end: formattedDates[formattedDates.length - 1]
        });
      } else {
        // dates is a single timestamp (e.g., for a Day Activity)
        const formattedDates = moment(itinerary_.dates.seconds * 1000).format("DD MMM YYYY");
        dateB = moment(formattedDates);
        dateC = moment(formattedDates);
        setDates({ start: formattedDates, end: formattedDates });
      }

      // Ensure at least one day for same-day itineraries
      let numDays = dateC.diff(dateB, 'days');
      if (numDays < 0) {
        numDays = 0;
      }
      const totalDays = numDays + 1;
      setNumberOfDays(totalDays);

      const location_data = filterByProperty(serviceListing, "location", itinerary_.location);
      const acc_data = filterByProperty(location_data, "type", itinerary_.typeofstay);
      const food_data = filterByProperty(location_data, "type", "Restaurants");
      const activity_data = filterByProperty(location_data, "type", "Things To Do");

      setAccomodationList(rankByInterests(acc_data, itinerary_.interests));

      /**
       * Ordered by how well each listing answers `interests`, which the quiz
       * has always collected and nothing ever read.
       *
       * This was `shuffle(food_data)`. Two things were wrong with it: it
       * ignored the traveller's stated interests entirely, and it did not even
       * randomise — `shuffle` mutates its argument and returns the same
       * reference, so every day and every time slot stored a pointer to one
       * array and rendered one identical order. `rankByInterests` is pure and
       * takes the day index, so each day genuinely differs.
       */
      const interests = itinerary_.interests;
      const forDay = (index) => ({
        restaurants: rankByInterests(food_data, interests, index),
        activities: rankByInterests(activity_data, interests, index),
      });

      const getSchedule = (index) => {
        if (totalDays > 1 && index === 0) {
          return [
            {
              dayTime: 'Arrival',
              time: 'All Day',
              ...forDay(index),
              description: pickRandomStringWithoutRepeating(timelineArrivalData)
            }
          ]
        }
        return [
          {
            dayTime: 'Morning',
            time: '6:00 am',
            ...forDay(index),
            description: pickRandomStringWithoutRepeating(timelineMorningData)
          },
          {
            dayTime: 'Afternoon',
            time: '12:00 pm',
            ...forDay(index + 1),
            description: pickRandomStringWithoutRepeating(timelineAfternoonData)
          },
          {
            dayTime: 'Evening',
            time: '5:00 pm',
            ...forDay(index + 2),
            description: pickRandomStringWithoutRepeating(timelineEveningData)
          },
        ]
      }
      /**
       * Reuse the stored plan if there is one; only generate when there is not.
       *
       * The slot descriptions are drawn at random, so regenerating on every
       * mount meant the same itinerary described itself differently on every
       * visit. That was invisible while the plan was private. It stops being
       * invisible the moment a copy is published to other people and emailed as
       * final: guests watched the trip churn for no reason, and the "final
       * copy" was a roll of the dice the owner would never see again.
       *
       * Persisted once, then read back. Never regenerated over a stored plan.
       */
      const stored = Array.isArray(itinerary_.schedule) ? itinerary_.schedule : [];
      const hasStoredPlan = stored.length === totalDays && stored.every((d) => d && d.schedule);

      const finalSchedule = hasStoredPlan
        ? stored
        : Array.from(Array(totalDays), (_, index) => ({
            day: index + 1,
            schedule: getSchedule(index)
          }));

      setSchedule(finalSchedule)

      if (!hasStoredPlan) saveItinerarySchedule(itinerary_.id, finalSchedule);

      if (itinerary_?.travelers) {
        setAdults(itinerary_.travelers.adults)
        setChildren(itinerary_.travelers.children)
        setInfants(itinerary_.travelers.infants)
      }

    }
    // Rebuilds the schedule when the ITINERARY changes. `serviceListing` is a
    // live Firestore subscription, so listing it would reshuffle the traveller's
    // restaurants and activities under them on every snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itinerary_]);

  const finalized = !!(itinerary_ && itinerary_.finalized);
  const dateRange = dates.start && dates.end && dates.end !== dates.start
    ? `${dates.start} - ${dates.end}`
    : dates.start;

  const liveInviteCount = invites.filter((i) => !i.revoked).length;
  const shareable = canInviteToItinerary(itinerary_);
  const notShareableBecause = whyNotShareable(itinerary_, 'ITINERARY');

  return (
    <PageTemplate>
      <Main>
        <Toolbar sx={{ marginBottom: '1em' }} />

        <Box sx={{ px: { xs: 2, sm: 3 } }}>
          <Button
            component={Link}
            to='/itinerary-list'
            variant="text"
            startIcon={<ChevronLeftIcon />}
          >
            Back
          </Button>
        </Box>

        {!itinerary_ ? (
          <Item elevation={1}>
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {itineraryList.length ? 'Itinerary not found.' : 'Loading your itinerary…'}
            </Typography>
          </Item>
        ) : (
          <>
            <Item elevation={1}>
              <Typography component="h1" className='txt--gray'>
                Finalise Your Itinerary
              </Typography>
              {dateRange && (
                <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                  {dateRange}
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
                {itinerary_.location && (
                  <Typography sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, color: 'text.primary', mr: 0.5 }}>
                    <LocationOnRoundedIcon sx={{ fontSize: '1.1rem', mr: 0.5, color: 'text.secondary' }} />
                    {itinerary_.location}
                  </Typography>
                )}
                {itinerary_.vacationtype && <Chip label={itinerary_.vacationtype} size="small" color="primary" variant="outlined" />}
                {itinerary_.typeoftravelers && <Chip label={itinerary_.typeoftravelers} size="small" variant="outlined" />}
                <Chip label={numberOfDays === 1 ? '1 day' : `${numberOfDays} days`} size="small" variant="outlined" />
              </Box>

              {itinerary_.bookings?.accommodation?.name && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Staying at {itinerary_?.bookings?.accommodation?.name}
                </Typography>
              )}

              {finaliseError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {finaliseError}
                </Alert>
              )}
              {sentNotice && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSentNotice('')}>
                  {sentNotice}
                </Alert>
              )}

              {/* Finalising belongs to whoever created the trip. A companion
                  never sees the control at all — a disabled button they cannot
                  explain is worse than no button. */}
              {isOwner ? (
                <Button
                  variant="contained"
                  disabled={finalising}
                  onClick={() => setOpenDialog(true)}
                  startIcon={finalized ? <CheckRoundedIcon /> : null}
                >
                  {finalised_label(finalized, finalising)}
                </Button>
              ) : (
                <Alert severity="info">
                  Only the person who created this trip can finalise it.
                </Alert>
              )}

              {/* Preview, then confirm — one dialog, two steps, because the
                  brief asks for the plan to be seen before it is settled and a
                  preview the traveller has to go and find is a preview nobody
                  looks at. This renders the SAME component the shared link
                  renders, so what the owner approves is what the guests get. */}
              <Dialog
                open={openDialog}
                onClose={() => !finalising && setOpenDialog(false)}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                aria-labelledby="finalise-dialog-title"
              >
                <DialogTitle id="finalise-dialog-title">
                  {itinerary_.finalized ? 'Update Itinerary' : 'Check your trip before you finalise'}
                </DialogTitle>

                <DialogContent dividers>
                  {itinerary_.finalized ? (
                    <DialogContentText>
                      Would you like to reopen this itinerary for changes? Everyone you invited will
                      see it as being planned again.
                    </DialogContentText>
                  ) : (
                    <>
                      <DialogContentText sx={{ mb: 2 }}>
                        This is exactly what {liveInviteCount === 0
                          ? 'anyone you invite'
                          : liveInviteCount === 1
                            ? 'the person you invited'
                            : `the ${liveInviteCount} people you invited`} will see.
                      </DialogContentText>

                      <TripPreview
                        snapshot={buildItinerarySnapshot({ ...itinerary_, finalized: true }, schedule)}
                        dense
                      />

                      {liveInviteCount > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Finalising sends the final copy to everyone on your guest list who gave an
                          email address.
                        </Alert>
                      )}
                    </>
                  )}
                </DialogContent>

                <DialogActions>
                  <Button onClick={() => setOpenDialog(false)} disabled={finalising}>
                    {itinerary_.finalized ? 'No' : 'Keep editing'}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={finalising}
                    onClick={() => {
                      setOpenDialog(false);
                      handleFinalizeItinerary(!itinerary_.finalized);
                    }}
                    autoFocus
                  >
                    {itinerary_.finalized ? 'Yes' : 'Finalise trip'}
                  </Button>
                </DialogActions>
              </Dialog>
            </Item>
            <Item elevation={1}>
              <Typography sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, color: 'text.primary', mr: 0.5 }}>
                <PeopleRoundedIcon sx={{ fontSize: '1.1rem', mr: 0.5, color: 'text.secondary' }} />
                Number of Travelers
              </Typography>
              <Box
                noValidate
                autoComplete="off"
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
              >
                <TextField disabled={finalized} id="adults" label="Adults" variant="outlined" value={adults} sx={{ marginRight: '12px', marginTop: '12px' }} type='number' onChange={handleAdults} />
                <TextField disabled={finalized} id="children" label="Children" variant="outlined" value={children} sx={{ marginRight: '12px', marginTop: '12px' }} type='number' onChange={handleChildren} />
                <TextField disabled={finalized} id="infants" label="Infants" variant="outlined" value={infants} sx={{ marginRight: '12px', marginTop: '12px' }} type='number' onChange={handleInfants} />


                <Button
                  variant="text"
                  disabled={itinerary_?.finalized}
                  onClick={() => saveTravelers()}
                  startIcon={<SaveRoundedIcon />}
                >
                  Save
                </Button>
              </Box>

            </Item>


            {/* Sharing. Rendered only for the owner, and only above the day
                plan — a guest list under the schedule is a guest list nobody
                scrolls to. TripCompanions returns null by itself on a solo
                trip that has never been shared, so the solo rule lives in one
                place rather than being restated here. */}
            {isOwner && (
              <TripCompanions
                ownerUid={user?.uid}
                tripId={itinerary_.id}
                tripType="ITINERARY"
                shareable={shareable}
                blockedReason={notShareableBecause}
                buildSnapshot={() => buildItinerarySnapshot(itinerary_, schedule)}
                onShareReady={handleShareReady}
              />
            )}

            {/* Only where the traveller is actually staying a night. The stay
                question is Holiday-only in the recommendation quiz, so a Day
                Activity itinerary carries no `typeofstay` and this row would
                otherwise render the heading above a permanent "No options
                available" — offering a bed for a trip that has no night in it.
                Keyed off the answer, not the route, so it stays correct if an
                admin puts the question back on both routes. */}
            {itinerary_.typeofstay && (
              <HorizontalSelectableCards title="Pick a place to stay" list={accomodationList} handleClick={(value) => selectAccommodation(value)} selected={itinerary_?.bookings?.accommodation?.name} disabled={itinerary_?.finalized} />
            )}

            {schedule.length > 0 ? (
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: { xs: 1, sm: 2 }, mt: 1 }}>
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    aria-label="Itinerary days"
                    sx={{
                      // Keeps "Day 1" flush left once the row has enough days to scroll.
                      ...scrollableTabsSx,
                      minHeight: 44,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        color: 'text.secondary',
                        minHeight: 44,
                      },
                      '& .Mui-selected': { color: 'primary.main' },
                      '& .MuiTabs-indicator': {
                        height: 3,
                        borderRadius: '3px 3px 0 0',
                        backgroundColor: 'primary.main',
                      },
                    }}
                  >
                    {schedule.map((item) => <Tab key={`itinerary-tab-${item.day}`} label={`Day ${item.day}`} />)}
                  </Tabs>
                </Box>
                <Box sx={{ mx: { xs: 1, sm: 2 } }}>
                  {schedule.map((item, i) => (
                    <CustomTabPanel key={`itinerary-tab-panel-${i}`} value={value} index={i}>
                      <Timeline schedule={item.schedule} />
                    </CustomTabPanel>
                  ))}
                </Box>
              </>
            ) : (
              <Typography color="text.secondary" sx={{ px: 3, py: 4, textAlign: 'center' }}>
                No days to show yet.
              </Typography>
            )}
            {/* The thread only exists once the trip has actually been shared.
                Before that there is nobody to talk to, and an empty comment box
                on a private plan is furniture. The owner posts as "Trip
                organiser" rather than under their email address — a companion
                who was invited by name should not be handed the owner's login. */}
            {isOwner && share?.threadId && (
              <TripComments
                threadId={share.threadId}
                inviteId={null}
                authorName="Trip organiser"
                canModerate
                readOnly={finalized}
              />
            )}

            <Item elevation={0}>
              <Button
                variant="contained"
                onClick={() => deleteItinerary(itinerary_.id)}
                startIcon={finalized ? <CheckRoundedIcon /> : null}
              >
                Delete Itinerary
              </Button>
            </Item>

          </>
        )}
      </Main>
    </PageTemplate>
  );
}
